import React, { useState } from "react";
import axios from "axios";

const Tabs = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({});
    const [validationError, setValidationError] = useState("");

    const requiredFieldsByStep = [
        ["loanType"],
        ["loanAmount"],
        ["incomeType"],
        ["income"],
        ["mobileNumber"],
        ["cibilScore", "source", "date"],
    ];

    const isStepComplete = (step) =>
        (requiredFieldsByStep[step] || []).every(
            (field) => String(formData[field] || "").trim() !== ""
        );

    // A quick, indicative assessment only. Final approval is always decided
    // by the lending partner after document and profile verification.
    const creditScore = Number(formData.cibilScore);
    const monthlyIncome = Number(formData.income);
    const requestedAmount = Number(formData.loanAmount);
    const isEligible =
        creditScore >= 650 &&
        monthlyIncome >= 15000 &&
        requestedAmount <= monthlyIncome * 36;

    const handleNext = () => {
        if (!isStepComplete(activeTab)) {
            setValidationError("Please complete this step before continuing.");
            return;
        }
        setValidationError("");
        setActiveTab((prev) => prev + 1);
    };

    const handleSubmit = async () => {
        const incompleteStep = requiredFieldsByStep.findIndex(
            (_, step) => !isStepComplete(step)
        );

        if (incompleteStep !== -1) {
            setValidationError("Please complete all six steps before submitting.");
            setActiveTab(incompleteStep);
            return;
        }

        const dataToSend = {
            MobileNo: formData.mobileNumber,
            LoanType: formData.loanType,
            LoanAmount: formData.loanAmount,
            IncomeType: formData.incomeType,
            Income: parseFloat(formData.income),
            CibilScore: formData.cibilScore,
            Source: formData.source,
            Date: formData.date,
        };

        try {
            await axios.post(
                "http://77.37.45.224:5000/api/form/eligibilityForm",
                dataToSend
            );

            setFormData({});
            setActiveTab(0);
            setValidationError("");
        } catch (error) {
            console.error("Error submitting form:", error);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setValidationError("");
    };

    /* 🔹 INPUT STYLE (LIGHT) */
    const inputClass =
        "mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";

    const tabs = [
        {
            title: "Your Loan Type",
            content: (
                <select
                    className={inputClass}
                    name="loanType"
                    value={formData.loanType || ""}
                    onChange={handleInputChange}
                >
                    <option value="">Select loan type</option>
                    <option value="Personal loan">Personal Loan</option>
                    <option value="Home loan">Home Loan</option>
                </select>
            ),
        },
        {
            title: "Your Loan Amount",
            content: (
                <input
                    className={inputClass}
                    type="number"
                    placeholder="Enter loan amount"
                    name="loanAmount"
                    value={formData.loanAmount || ""}
                    onChange={handleInputChange}
                />
            ),
        },
        {
            title: "Your Income Type",
            content: (
                <select
                    className={inputClass}
                    name="incomeType"
                    value={formData.incomeType || ""}
                    onChange={handleInputChange}
                >
                    <option value="">Select income type</option>
                    <option value="Salaried">Salaried</option>
                    <option value="Business">Business</option>
                </select>
            ),
        },
        {
            title: "Your Monthly Income",
            content: (
                <input
                    className={inputClass}
                    type="number"
                    placeholder="Enter monthly income"
                    name="income"
                    value={formData.income || ""}
                    onChange={handleInputChange}
                />
            ),
        },
        {
            title: "Your Mobile Number",
            content: (
                <input
                    className={inputClass}
                    type="text"
                    placeholder="Enter mobile number"
                    name="mobileNumber"
                    value={formData.mobileNumber || ""}
                    onChange={handleInputChange}
                />
            ),
        },
        {
            title: "Check Eligibility",
            content: (
                <div className="grid gap-3 sm:grid-cols-3">
                    <input
                        className={inputClass}
                        type="number"
                        placeholder="CIBIL score"
                        name="cibilScore"
                        value={formData.cibilScore || ""}
                        onChange={handleInputChange}
                    />
                    <input
                        className={inputClass}
                        type="text"
                        placeholder="Source"
                        name="source"
                        value={formData.source || ""}
                        onChange={handleInputChange}
                    />
                    <input
                        className={inputClass}
                        type="date"
                        name="date"
                        value={formData.date || ""}
                        onChange={handleInputChange}
                    />
                </div>
            ),
        },
        {
            title: "Your eligibility result",
            content: (
                <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center">
                    {isEligible ? (
                        <>
                            <p className="text-base font-bold text-emerald-600">
                                Good news — you appear eligible.
                            </p>
                            <p className="text-xs leading-relaxed text-slate-600">
                                Based on your details, you meet our preliminary criteria.
                                Submit your request and we will help you explore suitable offers.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-base font-bold text-amber-600">
                                Let&apos;s find a way forward.
                            </p>
                            <p className="text-xs leading-relaxed text-slate-600">
                                Your details do not meet our instant assessment criteria, but
                                a finance expert can review alternatives and call you shortly.
                            </p>
                        </>
                    )}
                    <p className="border-t border-slate-200 pt-3 text-[10px] leading-relaxed text-slate-500">
                        This is an indicative assessment, not a final loan approval.
                    </p>
                </div>
            ),
        },
    ];

    const totalSteps = tabs.length;

    return (
        <div>
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <div>
                    {/* <p className="text-[11px] uppercase tracking-[0.25em] text-emerald-600">
                        Eligibility Check
                    </p> */}
                    <p className="text-xs text-slate-500">
                        Step {activeTab + 1} of {totalSteps}
                    </p>
                </div>

                {/* Progress */}
                <div className="flex gap-1.5">
                    {tabs.map((_, i) => (
                        <span
                            key={i}
                            className={`h-1.5 rounded-full transition-all ${i === activeTab
                                    ? "w-4 bg-emerald-500"
                                    : "w-2 bg-emerald-200"
                                }`}
                        />
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="mb-5 space-y-3">
                <h3 className="text-sm font-semibold text-slate-800">
                    {tabs[activeTab].title}
                </h3>
                {tabs[activeTab].content}
                {validationError && (
                    <p className="text-xs font-medium text-red-600" role="alert">
                        {validationError}
                    </p>
                )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    disabled={activeTab === 0}
                    onClick={() => setActiveTab((prev) => Math.max(prev - 1, 0))}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold ${activeTab === 0
                            ? "bg-emerald-200 text-emerald-700 opacity-60 cursor-not-allowed"
                            : "bg-emerald-500 text-white"
                        }`}
                >
                    Previous
                </button>

                {activeTab !== totalSteps - 1 ? (
                    <button
                        type="button"
                        onClick={handleNext}
                        className="rounded-full bg-emerald-500 px-6 py-2 text-xs font-semibold uppercase text-white"
                    >
                        Next
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="rounded-full bg-emerald-600 px-6 py-2 text-xs font-semibold uppercase text-white"
                    >
                        Submit
                    </button>
                )}
            </div>
        </div>
    );
};

export default Tabs;
